import React from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Divider, TextField, IconButton, CircularProgress, Paper, Button, Drawer, useMediaQuery, FormControlLabel, Switch } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext';
import { db } from '../utils/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

export default function ChatPage() {
  const { user, role } = useAuth();
  const [threads, setThreads] = React.useState([]);
  const [loadingThreads, setLoadingThreads] = React.useState(true);
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const messagesBoxRef = React.useRef(null);
  const [search, setSearch] = React.useState('');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);
  const [senderNames, setSenderNames] = React.useState({}); // uid -> display name/email

  const base = (process.env.REACT_APP_BACKEND_URL || '');
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileInboxOpen, setMobileInboxOpen] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);

  const loadThreads = React.useCallback(async () => {
    if (!user) return;
    setLoadingThreads(true);
    try {
      const token = await user.getIdToken();
      const includeArchived = (role === 'admin' && showArchived) ? '?includeArchived=true' : '';
      const resp = await fetch(`${base}/api/messages/threads${includeArchived}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await resp.json();
      if (resp.ok) {
        const list = Array.isArray(j.threads) ? j.threads : [];
        setThreads(list);
        if (role === 'admin') {
          if (list?.[0]?.userId && !selectedUserId) setSelectedUserId(list[0].userId);
        } else {
          // Regular users: ensure a selection so they can start chatting even if thread doesn't exist yet
          if (list?.[0]?.userId && !selectedUserId) setSelectedUserId(list[0].userId);
          if ((!list || list.length === 0) && !selectedUserId) setSelectedUserId(user.uid);
        }
      }
    } catch (_) { /* ignore */ }
    setLoadingThreads(false);
  }, [user, role, base, selectedUserId, showArchived]);

  const loadMessages = React.useCallback(async (uid, { silent = false } = {}) => {
    if (!user || !uid) return;
    if (!silent) setLoadingMessages(true);
    if (silent) setIsRefreshing(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${base}/api/messages/thread/${encodeURIComponent(uid)}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await resp.json();
      if (resp.ok) {
  const list = Array.isArray(j.messages) ? j.messages : [];
        setMessages(prev => {
          // If lengths differ or last IDs differ, we'll decide whether to scroll
          const prevLast = prev.length ? prev[prev.length - 1].id : null;
          const nextLast = list.length ? list[list.length - 1].id : null;
          const el = messagesBoxRef.current;
          const nearBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight) < 80 : true;
          const changed = prevLast !== nextLast || prev.length !== list.length;
          // After state applies, scroll if user near bottom or it is an initial load
          setTimeout(() => {
            if (!messagesBoxRef.current) return;
            if (nearBottom || prev.length === 0) {
              messagesBoxRef.current.scrollTo({ top: messagesBoxRef.current.scrollHeight, behavior: (prev.length ? 'smooth' : 'auto') });
            }
          }, 0);
          return list;
        });
        // Warm sender names cache
        const uniqueIds = Array.from(new Set(list.map(m => m.senderId).filter(Boolean)));
        const toFetch = uniqueIds.filter(id => !senderNames[id]);
        // Prime cache with thread user name if available
        const thread = threads.find(t => t.userId === uid);
        if (thread && thread.userId && (thread.userName || thread.userEmail) && !senderNames[thread.userId]) {
          setSenderNames(prev => ({ ...prev, [thread.userId]: thread.userName || thread.userEmail }));
        }
  // Do not cache current user's name; we'll always show "Me"
        if (toFetch.length) {
          // Fetch missing names in background
          Promise.all(toFetch.map(async uid => {
            try {
              const snap = await getDoc(doc(db, 'users', uid));
              const data = snap.exists() ? snap.data() : null;
              const name = data?.name || data?.email || undefined;
              if (name) return { uid, name };
            } catch (_) { /* ignore */ }
            return { uid, name: undefined };
          })).then(results => {
            setSenderNames(prev => {
              const next = { ...prev };
              results.forEach(r => { if (r.name && !next[r.uid]) next[r.uid] = r.name; });
              return next;
            });
          });
        }
      }
    } catch (_) { /* ignore */ }
    if (!silent) setLoadingMessages(false);
    if (silent) setIsRefreshing(false);
  }, [user, base]);

  React.useEffect(() => { loadThreads(); }, [loadThreads]);
  // Safety: for non-admins, ensure we always have a selected id for composing
  React.useEffect(() => {
    if (user && role !== 'admin' && !selectedUserId) setSelectedUserId(user.uid);
  }, [user, role, selectedUserId]);
  // Clear messages immediately when switching threads (or clearing selection)
  React.useEffect(() => { setMessages([]); }, [selectedUserId]);
  // When toggling archived filter, clear selection and messages for a clean state
  React.useEffect(() => {
    if (role === 'admin') {
      setSelectedUserId(null);
      setMessages([]);
    }
  }, [showArchived, role]);
  React.useEffect(() => { if (selectedUserId) loadMessages(selectedUserId); }, [selectedUserId, loadMessages]);

  const sendMessage = async () => {
    if (!input.trim() || !selectedUserId) return;
    setPosting(true);
    try {
      const token = await user.getIdToken();
  const target = (role === 'admin') ? selectedUserId : 'me';
  const resp = await fetch(`${base}/api/messages/thread/${encodeURIComponent(target)}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: input.trim() })
      });
      if (resp.ok) {
        setInput('');
        await loadMessages(selectedUserId);
        await loadThreads();
      }
    } catch (_) { /* ignore */ }
    setPosting(false);
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearch(value);
    if (!value) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const usersCol = collection(db, 'users');
      const snaps = await getDocs(usersCol);
      const list = snaps.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => (u.role || 'user') !== 'admin');
      const results = list.filter(u => (
        (u.name && u.name.toLowerCase().includes(value.toLowerCase())) ||
        (u.email && u.email.toLowerCase().includes(value.toLowerCase()))
      ));
      setSearchResults(results);
    } finally {
      setSearchLoading(false);
    }
  };

  const startThreadFor = async (u) => {
    if (!u?.uid) return;
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${base}/api/messages/thread/${encodeURIComponent(u.uid)}/init`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) return;
      await loadThreads();
  setMessages([]);
  setSelectedUserId(u.uid);
      setSearch('');
      setSearchResults([]);
    } catch (_) { /* ignore */ }
  };

  // Real-time updates
  React.useEffect(() => {
    let cleanup = () => {};
    let pollId;
    (async () => {
      if (!selectedUserId || !user) return;
      // For admins, try SSE; for regular users, use polling only to avoid potential auth/cors noise
      if (role === 'admin') {
        try {
          const token = await user.getIdToken();
          const baseUrl = `${base}/api/messages/thread/${encodeURIComponent(selectedUserId)}/stream?access_token=${encodeURIComponent(token)}`;
          const es = new EventSource(baseUrl, { withCredentials: false });
          es.onmessage = () => {};
          es.addEventListener('messages', (evt) => {
            try {
              const payload = JSON.parse(evt.data);
              const list = Array.isArray(payload.messages) ? payload.messages : [];
              setMessages(prev => {
                const el = messagesBoxRef.current;
                const nearBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight) < 80 : true;
                setTimeout(() => {
                  if (!messagesBoxRef.current) return;
                  if (nearBottom || prev.length === 0) {
                    messagesBoxRef.current.scrollTo({ top: messagesBoxRef.current.scrollHeight, behavior: (prev.length ? 'smooth' : 'auto') });
                  }
                }, 0);
                return list;
              });
            } catch (_) {}
          });
          es.onerror = () => { try { es.close(); } catch (_) {} };
          cleanup = () => { try { es.close(); } catch (_) {} };
        } catch (_) {}
      }
      // Polling (admins and users)
      pollId = setInterval(() => loadMessages(selectedUserId, { silent: true }), 15000);
    })();
    return () => { cleanup(); if (pollId) clearInterval(pollId); };
  }, [selectedUserId, user, base, loadMessages, role]);

  const setArchive = async (uid, archived) => {
    if (!uid) return;
    try {
      const token = await user.getIdToken();
      const path = archived ? 'archive' : 'unarchive';
      const resp = await fetch(`${base}/api/messages/thread/${encodeURIComponent(uid)}/${path}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) {
  // Clear chat view on archive/unarchive
  setMessages([]);
  await loadThreads();
        if (archived && !showArchived && selectedUserId === uid) {
          setSelectedUserId(null);
        }
      }
    } catch (_) { /* ignore */ }
  };

  if (!user) return <Typography sx={{ p: 2 }}>Please log in to view messages.</Typography>;

  const renderInbox = () => (
    <>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Inbox</Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search users by name or email"
          value={search}
          onChange={handleSearch}
        />
        {role === 'admin' && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch size="small" checked={showArchived} onChange={(_, v) => setShowArchived(v)} />}
            label={<Typography variant="caption">Show archived</Typography>}
          />
        )}
        {searchLoading && <Typography variant="caption">Searching...</Typography>}
        {!searchLoading && search && searchResults.length === 0 && (
          <Typography variant="caption">No users found.</Typography>
        )}
        {searchResults.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {searchResults.map(u => (
              <Box key={u.uid} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
                <Typography variant="body2">{u.name || u.email} ({u.email || u.uid})</Typography>
                <Button size="small" variant="contained" onClick={() => { startThreadFor(u); setMobileInboxOpen(false); }}>Start</Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Divider />
      {loadingThreads ? (
        <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>
      ) : (
        <List dense sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          {threads.length === 0 && <Box sx={{ p: 2 }}><Typography variant="body2">No threads yet.</Typography></Box>}
          {threads.map(t => (
            <ListItemButton key={t.id} selected={selectedUserId === t.userId} onClick={() => { setMessages([]); setSelectedUserId(t.userId); setMobileInboxOpen(false); loadMessages(t.userId); }}>
              <ListItemText
                primary={`${t.userName || t.userEmail || t.userId}${t.archived ? ' • Archived' : ''}`}
                secondary={t.lastMessageText || 'No messages yet'}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </>
  );

  const nameFor = (m) => {
    // Current user always displayed as "Me"
    if (m.senderId === user.uid) return 'Me';
    // Prefer cached name for others
    if (senderNames[m.senderId]) return senderNames[m.senderId];
    if (m.senderRole === 'admin') return 'Admin';
    // For user role, fall back to thread label if present
    const thread = threads.find(t => t.userId === selectedUserId);
    if (thread) return thread.userName || thread.userEmail || m.senderId;
    return m.senderId;
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, sm: 2 } }}>
      {role === 'admin' && isMdUp && (
        <Paper sx={{ width: 320, flexShrink: 0, p: 0 }} variant="outlined">
          {renderInbox()}
        </Paper>
      )}

      {role === 'admin' && !isMdUp && (
        <Drawer anchor="left" open={mobileInboxOpen} onClose={() => setMobileInboxOpen(false)}>
          <Box sx={{ width: 320 }} role="presentation">
            {renderInbox()}
          </Box>
        </Drawer>
      )}

      <Paper sx={{ flex: 1, p: 0, overflow: 'hidden' }} variant="outlined">
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {role === 'admin' ? (threads.find(t => t.userId === selectedUserId)?.userName || threads.find(t => t.userId === selectedUserId)?.userEmail || selectedUserId || 'Select a thread') : 'Messages with Admins'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {role === 'admin' && !isMdUp && (
              <IconButton size="small" onClick={() => setMobileInboxOpen(true)} aria-label="Open inbox">
                <MenuIcon />
              </IconButton>
            )}
            {role === 'admin' && selectedUserId && (
              (() => {
                const th = threads.find(t => t.userId === selectedUserId);
                if (!th) return null;
                return (
                  <Button size="small" variant="outlined" onClick={() => setArchive(selectedUserId, !th.archived)}>
                    {th.archived ? 'Unarchive' : 'Archive'}
                  </Button>
                );
              })()
            )}
            {isRefreshing && <Typography variant="caption" color="text.secondary">Updating…</Typography>}
          </Box>
        </Box>
        <Divider />
        <Box ref={messagesBoxRef} sx={{ p: { xs: 1.25, sm: 2 }, minHeight: 360, maxHeight: { xs: '55vh', md: '60vh' }, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {loadingMessages && messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={20} /></Box>
          ) : messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No messages yet. Say hello!</Typography>
          ) : (
            messages.map(m => (
              <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: (m.senderId === user.uid ? 'flex-end' : 'flex-start') }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, maxWidth: '75%', textAlign: (m.senderId === user.uid ? 'right' : 'left') }}>
                  {nameFor(m)}
                </Typography>
                <Paper
                  sx={{
                    p: 1.25,
                    maxWidth: '75%',
                    bgcolor: (m.senderId === user.uid ? '#1976d2' : '#f1f1f1'),
                    color: (m.senderId === user.uid ? '#fff' : 'inherit'),
                    borderRadius: 2,
                    boxShadow: 0,
                    border: '1px solid',
                    borderColor: (m.senderId === user.uid ? 'rgba(25,118,210,0.4)' : '#e5e5e5'),
                    animation: 'fadeIn 140ms ease-out',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                </Paper>
              </Box>
            ))
          )}
        </Box>
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          />
          <IconButton color="primary" onClick={sendMessage} disabled={!input.trim() || posting || !selectedUserId}>
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
